// /sculpture-art-fabrication-los-angeles/ (ProjectServicePage). Was a
// Contentful page entry until 2026-11-04; gatsby-node now skips it.
// GSC Aug–Oct 2026: 31 clicks, 3.5K impressions. Queries: "los angeles art
// fabrication" (pos 2.8), "art fabrication los angeles" (3.3), "art
// fabrication la" (3.9), "art fabrication near me" (2.3), "art fabrication".
// City pages test demand for future locations (owner growth policy rule 3 in
// .seo/brand.md, confirmed by the coordinator 2026-11-04): name the city and
// the service only. No distance framing ("made in New York, shipped to LA",
// "no LA studio"), no local storefront or address, delivery and installation
// phrased neutrally. The page hides the studio addresses and its schema
// provider carries no address. Facts: the art rows in .seo/truth.md. Copy and
// FAQs are written for LA, not copied from /services/art-fabrication/.
const art = require('./art-fabrication')
const { pricing, reviewedDate } = require('./shared')

const photos = {
  ...art.photos,
  austinLeeShow: {
    ...art.photos.austinLeeShow,
    caption: 'Painted figure sculptures fabricated for a gallery show.',
  },
}

const page = {
  path: '/sculpture-art-fabrication-los-angeles/',
  title: 'Art Fabrication Los Angeles | Sculpture Fabrication',
  description:
    'Art and sculpture fabrication for Los Angeles artists, galleries and brands: 3D modeled, scanned, printed, painted or metal plated, then crated and delivered.',
  heading: 'Los Angeles Art Fabrication and Sculpture Fabrication',
  eyebrow: 'Sculpture and art fabrication · Los Angeles',
  answer:
    'PrintAWorld fabricates sculpture and art objects for artists, galleries and brands in Los Angeles. We turn sketches, photos, clay models, scans and 3D files into finished work with 3D modeling, 3D scanning, 3D printing, CNC milling, mold making, assembly, painting and metal plating, and we’ve made sculptures for Austin Lee, Coco Fusco and William Lamson. Large pieces are printed in sections. Finished work is packed or crated and delivered to your studio, gallery or fair. Every piece is quoted per project, with delivery in the quote.',
  hero: 'austinLeeShow',
  reviewedDate,
  crumbs: [
    { name: 'Art fabrication', path: '/services/art-fabrication/' },
    { name: 'Los Angeles' },
  ],
  service: {
    name: 'Art and Sculpture Fabrication in Los Angeles',
    serviceType: 'Art and sculpture fabrication',
    areaServed: [{ '@type': 'City', name: 'Los Angeles' }],
    providerAddress: false,
    description:
      'Art and sculpture fabrication for Los Angeles artists, galleries and brands: 3D modeling, scanning, printing, CNC milling, mold making, painting and metal plating, packed or crated and delivered to the venue.',
  },
  quoteLabel: 'Start an LA fabrication quote',
  showStudios: false,
}

const intro = {
  eyebrow: 'Art fabrication LA',
  heading: 'What does art fabrication for Los Angeles artists include?',
  paragraphs: [
    'You keep the concept and the decisions; we work out how to build the piece, make it and finish it. For Los Angeles artists and galleries that usually means a show piece, an art fair work, an edition, or a sculpture too big or too detailed to build alone in the studio.',
    'Most projects start as a sketch, photos or a 3D file, and approvals happen by email. Have a maquette to copy or enlarge? We can [3D scan it](/3d-scanning-services-los-angeles/), scale it and print it in sections. See also our main [art fabrication](/services/art-fabrication/) page.',
  ],
}

const glance = {
  heading: 'Los Angeles art fabrication at a glance',
  rows: [
    [
      'Who it’s for',
      'Los Angeles artists, galleries, brands and agencies making sculpture and art objects',
    ],
    [
      'Methods',
      '3D modeling, 3D scanning, 3D printing, CNC milling, mold making, assembly',
    ],
    ['Finishes', 'Sanding, priming, painting and metal plating'],
    ['Starting points', 'Sketches, photos, clay models, scans or 3D files'],
    ['Largest size', '6 feet and more, printed in sections and assembled'],
    ['Named work', 'Sculptures for Austin Lee, Coco Fusco and William Lamson'],
    [
      'How we work together',
      'Files by upload or link; review and approvals by email and phone',
    ],
    [
      'Delivery',
      'Packed or crated and delivered to your studio, gallery or fair',
    ],
    [
      'Price',
      `Quoted per project, delivery included in the quote. 3D modeling from $${pricing.modelingFrom}.`,
    ],
    [
      'Not included',
      'Installation and engineering, unless the quote lists them',
    ],
  ],
}

const work = {
  eyebrow: 'Selected work',
  heading: 'Sculpture fabrication for artists: selected work',
  photos: [
    'austinLeeTall',
    'lamson',
    'tinMan',
    'floatingChair',
    'emojiHands',
    'stack',
  ],
}

const offer = {
  eyebrow: 'What LA clients ask for',
  heading: 'What can we fabricate for Los Angeles artists and galleries?',
  index: '02 / THE WORK',
  items: [
    {
      label: 'L1',
      title: 'Gallery and show pieces',
      body: 'Finished sculpture for an opening, printed, assembled and painted or plated, and timed to the install date.',
    },
    {
      label: 'L2',
      title: 'Art fair work',
      body: 'Pieces packed for a fair booth, with the delivery window planned in the quote.',
    },
    {
      label: 'L3',
      title: 'Large sculpture in sections',
      body: 'Big figures and forms printed in sections that crate well and join cleanly.',
    },
    {
      label: 'L4',
      title: 'Editions and multiples',
      body: 'The same piece in a run, printed side by side and finished to match.',
    },
    {
      label: 'L5',
      title: 'Metal finishes',
      body: 'Chrome, gold and other metal finishes plated over the print, for pieces that should read as metal.',
    },
    {
      label: 'L6',
      title: 'Brand and set pieces',
      body: 'Sculptural objects for brand events, windows and shoots, made from your references.',
    },
  ],
}

const contrast = {
  eyebrow: 'Where to start',
  heading: 'Can you fabricate a sculpture from a sketch, maquette or scan?',
  paragraphs: [
    'Yes. Send sketches, photos, a scan or a 3D file; we model what’s missing and send it for your approval before anything is made. Files can be uploaded (up to 500 MB each) or sent as a link.',
    'A small maquette can become a 6-foot piece: we 3D scan it, scale it, print it in sections and finish it in paint or a metal plate.',
  ],
  links: [
    ['3D scanning in Los Angeles', '/3d-scanning-services-los-angeles/'],
    ['Metal plating', '/services/metal-plating/'],
  ],
}

const steps = {
  eyebrow: 'Step by step',
  heading: 'How art fabrication for Los Angeles works',
  items: [
    {
      title: 'Send files and a date',
      body: 'Sketches, photos, a scan or 3D file, plus size, finish, budget range and your install date.',
    },
    {
      title: 'Review and quote',
      body: 'We review by email or phone and send a written scope with price, schedule and delivery.',
    },
    {
      title: 'Approve, then we make it',
      body: 'You approve the model; we print, assemble and paint or plate the piece.',
    },
    {
      title: 'Crated and delivered',
      body: 'Packed or crated for the piece and delivered to your studio, gallery or fair.',
    },
  ],
}

const fit = {
  eyebrow: 'Is it the right fit?',
  heading: 'Are we the right art fabricator for your LA project?',
  good: [
    'Pieces that start as a sketch, photo, scan or 3D file',
    'Painted or metal-plated sculpture made by 3D modeling and printing',
    'Large work printed in sections and crated for delivery',
    'Editions and multiples finished to match',
    'Shows and fairs with a date you can share early',
  ],
  other: [
    'Cast bronze or poured metal: a foundry is the right partner (our metal finishes are plated)',
    'Structural public art that needs stamped engineering drawings',
    'On-site installation, unless your quote lists it',
    'Clear, glass-look finishes, which we don’t offer',
  ],
}

const pricing_ = {
  eyebrow: 'Budgeting your piece',
  heading: 'How much does art fabrication in Los Angeles cost?',
  lead: {
    label: 'Fabrication and delivery are',
    value: 'Quoted per project',
    note: 'One written quote covers the piece, packing or crating, and delivery to your venue.',
  },
  rowsHeading: 'Known starting points',
  rows: [
    ['3D modeling from references', `From $${pricing.modelingFrom}`],
    ['3D scanning a maquette or object', `From $${pricing.scanningFrom}`],
    ['3D printing from a ready file', `From about $${pricing.printFrom}`],
    ['Painting or metal plating', 'Quoted per project'],
    ['Packing or crating', 'Quoted for the size and fragility of the piece'],
    ['Delivery', 'Quoted to your ZIP code'],
  ],
  note: 'Send a budget range with your concept, and we’ll suggest a scope that fits, delivery included.',
}

const logistics = {
  eyebrow: 'Crating & delivery',
  heading: 'How is a finished sculpture delivered?',
  paragraphs: [
    'Small and medium pieces are boxed with padding. Large, heavy or fragile work is crated. The quote lists the packing and the delivery address: your studio, a gallery, an art handler or a fair booth.',
    'If a big piece is printed in sections, the quote says whether it travels joined or in sections, whichever is safer. Share loading dock, elevator and receiving hours early so delivery lands when the venue can take it.',
  ],
  aside: {
    eyebrow: 'Planning an opening?',
    title: 'Share the install date first.',
    body: 'Timing depends on size, method and finish, and metal plating takes the longest. The written quote sets the approval stages, the fabrication date and the delivery date.',
  },
}

const related = {
  heading: 'More for LA artists',
  links: [
    ['Art fabrication', '/services/art-fabrication/'],
    ['3D scanning in Los Angeles', '/3d-scanning-services-los-angeles/'],
    ['Metal plating', '/services/metal-plating/'],
    ['3D scanning', '/services/3d-scanning/'],
  ],
}

const faqs = [
  {
    question: 'Who offers art fabrication for artists in Los Angeles?',
    answer:
      'PrintAWorld fabricates sculpture and art objects for Los Angeles artists, galleries and brands with 3D modeling, scanning, printing, CNC milling, mold making, assembly, painting and metal plating. It has made sculptures for Austin Lee, Coco Fusco and William Lamson. Send your files or references for a written quote.',
  },
  {
    question: 'How much does art fabrication cost in Los Angeles?',
    answer: `Each piece is quoted per project, with packing or crating and delivery in the same quote. Known starting points: 3D modeling from $${pricing.modelingFrom}, 3D scanning from $${pricing.scanningFrom}, and 3D printing from about $${pricing.printFrom} for a ready file.`,
  },
  {
    question: 'Can you deliver a sculpture to an LA gallery or art fair?',
    answer:
      'Yes, to the address in your quote. Small pieces are boxed; large or fragile work is crated. Share loading dock, elevator, receiving hours and the install date early.',
  },
  {
    question: 'How long does it take to fabricate a piece for a show?',
    answer:
      'It depends on size, method and finish, and metal plating takes the longest. Tell us the install date first; the written quote sets the approval stages and the delivery date.',
  },
  {
    question: 'Do we have to meet in person?',
    answer:
      'No. Most projects run on files, photos, email and phone calls: you approve the model and the finish before we fabricate.',
  },
  {
    question: 'Can you copy or enlarge a maquette?',
    answer: `Yes. We 3D scan the maquette, scale it and fabricate the new piece; the quote sets how the original is handed over and returned. 3D scanning starts at $${pricing.scanningFrom}.`,
  },
  {
    question: 'Can you make a large sculpture?',
    answer:
      'Yes. Large pieces are printed in sections, joined, filled and finished; projects have reached 6 feet and more. The quote confirms how it splits, how it’s crated and how it’s delivered.',
  },
  {
    question: 'Do you install the work?',
    answer:
      'Installation isn’t included unless your quote lists it. Galleries often use their own team or an art handler; tell us who will receive and install the piece.',
  },
]

const checklist = [
  'Sketches, photos, a scan or 3D file (a link is fine)',
  'Target size, quantity or edition size, and the finish',
  'Delivery address: studio, gallery, handler or fair',
  'Loading dock, elevator and receiving hours',
  'Budget range and the install or opening date',
]

module.exports = {
  key: 'artLosAngeles',
  page,
  photos,
  intro,
  glance,
  work,
  offer,
  contrast,
  steps,
  fit,
  pricing: pricing_,
  logistics,
  related,
  faqs,
  faqHeading: 'What do Los Angeles artists ask about art fabrication?',
  checklist,
  quote: {
    eyebrow: 'Let’s make your piece',
    heading: ['Tell us about the work.', 'We’ll plan the build.'],
    body: 'Send what you have, the delivery address and your date. We reply with a written scope, price and delivery.',
  },
}
